import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Card, FormControl, FormLabel, Input, Option, Select, Stack, Typography, Modal, ModalDialog, DialogTitle, DialogContent, DialogActions, Autocomplete, Slider, Textarea } from '@mui/joy';
import { documentsService } from '../services/documents.service';
import {
  assertCanIssueCommercialDocument,
  resolveDocumentCustomerIdentity,
} from '../../../pages/project-workspace/workspaceBillingIdentity';
import LookupAdminPanel from '../components/LookupAdminPanel';
import { lookupsService } from '../services/lookups.service';
import type { DocumentKind, DocumentLineInput, DocumentFeesConfig } from '../types/documents.types';
import type { InvoiceRole } from '../utils/invoiceLedger';
import { computeTotals } from '../types/documents.types';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useCustomerStore } from '../../../store/customerStore';
import { secureApi } from '../../../lib/supabaseClient';
import type { Customer } from '../../../services/customerService';
import { CreateWizard } from '../components/CreateWizard';
import { EditWizard } from '../components/EditWizard';
import { listDocumentShareLinks, type DocumentShareLinkRecord } from '../services/documentShareLinks.service';
import { visualDesignerSurveysService } from '../services/visualDesignerSurveys.service';
import type { VisualDesignerPairingModel } from '../components/VisualDesignerPairingCard';
import { isVisualDesignerLine } from '../utils/visualDesignerSurveyClone';
import { planVersionSaveLines } from '../utils/versionSaveLines';
import { isWorkspaceDocument, workspaceDocumentPath } from '../utils/workspaceDocumentAccess';
import { ensureProjectLifecycleRef } from '../../../pages/project-workspace/workspaceLifecycleRef';
import { resolveWorkspaceBrandId } from '../../../pages/project-workspace/workspaceBrand';
import { defaultDepositTerms, isWorkspaceSupplyOnly } from '../../../pages/project-workspace/workspaceSupplyOnly';
import { organisationSettings } from '../../../services/organisationSettings';
import { useBrands } from '../../../hooks/useBrands';
import {
  buildVersionSnapshot,
  editorLinesFromMapped,
  hydrateDepositAndFees,
  mapApiLinesToEditor,
  stringifyVersionSnapshot,
  type VersionSnapshotInput,
} from '../utils/wizardDirtySnapshot';

function normalizeKindParam(raw: string | null): string | null {
  return String(raw || '').trim().toLowerCase().startsWith('invoice') ? 'Invoice' : null;
}

export type WizardLeaveHandle = {
  requestLeave: (proceed: () => void, options?: { afterSave?: 'proceed' | 'keep' }) => void;
  save: () => Promise<boolean>;
  openHelp: () => void;
};

interface Props {
  initialKind?: DocumentKind;
  projectId?: string;
  customerId?: string;
  documentId?: string;
  isEditMode?: boolean;
  embedded?: boolean;
  versionLayout?: 'horizontal' | 'vertical';
  lockCustomer?: boolean;
  lockKind?: boolean;
  preferredLifecycleRef?: string;
  /** Prefill document title on create (e.g. workspace name). */
  preferredTitle?: string;
  preferredInvoiceRole?: InvoiceRole | null;
  preferredSeedLines?: DocumentLineInput[];
  lockLifecycleRef?: boolean;
  leaveGuardRef?: React.MutableRefObject<WizardLeaveHandle | null>;
  onClose?: () => void;
  onDocumentReady?: (documentId: string) => void;
  visualSurveyId?: string | null;
  visualSurveyLabel?: string | null;
  visualDesignerLines?: DocumentLineInput[];
  visualSurveyAttachable?: boolean;
  visualSurveys?: VisualDesignerPairingModel['surveys'];
  visualDesignerPairingLocked?: boolean;
  onVisualSurveyChange?: (id: string) => void;
  onOpenVisualDesigner?: () => void;
  lockRefreshKey?: number;
  onVersionLockChange?: (lock: {
    id: string;
    versionNumber: number;
    isTransmitted: boolean;
    overrideUnlocked: boolean;
    visualSurveyId?: string | null;
  } | null) => void;
  onVersionsChanged?: () => void;
  preferredVersionId?: string | null;
  hideVersions?: boolean;
  /** Hide Unlock/Relock chrome — survey workspace uses Reopen Final / immutable First instead. */
  omitLockControls?: boolean;
  /** Workspace already has the project — skip a second login-token fetch on save. */
  workspaceProject?: any;
}

const CREATE_BODY_DEFAULT = JSON.stringify({
  title: '',
  description: '',
  notes: '',
  fees: { adminPct: 0.10, adminCap: 200, contingencyPct: 0.10, profitPct: 0.10, developerPct: null },
  vatEnabled: true,
  vatPct: 0.20,
  inputVatSelected: false,
  depositEnabled: true,
  depositMode: 'percent',
  depositPct: 0.40,
  depositFixed: 0,
  paymentStructureApplicable: false,
  paymentStructurePattern: null,
  displayDays: null,
  displayHours: null,
  lines: [],
});

type WizardVersion = {
  id: string;
  versionNumber: number;
  isActive: boolean;
  isTransmitted: boolean;
  transmittedAt: string | null;
  overrideUnlocked: boolean;
  overrideUnlockedAt: string | null;
  visualSurveyId: string | null;
};

function versionLockFromRecord(record: any): { isTransmitted: boolean; overrideUnlocked: boolean } {
  return {
    isTransmitted: !!(record?.isTransmitted ?? record?.is_transmitted),
    overrideUnlocked: !!(record?.overrideUnlocked ?? record?.override_unlocked),
  };
}

export const EstimateQuoteWizard: React.FC<Props> = ({
  initialKind = 'quote',
  projectId: propProjectId,
  customerId,
  documentId: propDocumentId,
  isEditMode = false,
  embedded = false,
  versionLayout = 'horizontal',
  lockCustomer = false,
  lockKind = false,
  preferredLifecycleRef,
  preferredTitle,
  preferredInvoiceRole = null,
  preferredSeedLines,
  lockLifecycleRef = false,
  leaveGuardRef,
  onClose,
  onDocumentReady,
  visualSurveyId = null,
  visualSurveyLabel = null,
  visualDesignerLines = [],
  visualSurveyAttachable = false,
  visualSurveys = [],
  visualDesignerPairingLocked = false,
  onVisualSurveyChange,
  onOpenVisualDesigner,
  lockRefreshKey = 0,
  onVersionLockChange,
  onVersionsChanged,
  preferredVersionId = null,
  hideVersions = false,
  omitLockControls = false,
  workspaceProject: workspaceProjectProp = null,
}) => {
  const navigate = useNavigate();
  const params = useParams<{ projectId?: string; documentId?: string }>();
  const [searchParams] = useSearchParams();
  const projectId = params.projectId || propProjectId;
  const documentId = propDocumentId || params.documentId;

  useEffect(() => {
    if (embedded || isEditMode || !params.projectId) return;
    const path = workspaceDocumentPath({ projectId: params.projectId, kind: normalizeKindParam(searchParams.get('kind')) });
    if (path) navigate(path, { replace: true });
  }, [embedded, isEditMode, params.projectId, searchParams, navigate]);  
  const normalizeKind = (k: string | null | undefined): DocumentKind | null => {
    if (!k) return null;
    const v = String(k).toLowerCase();
    if (v === 'estimate' || v === 'estimates') return 'Estimate';
    if (v === 'quotation' || v === 'quote' || v === 'quotes') return 'Quotation';
    if (v === 'invoice' || v === 'invoices') return 'Invoice';
    if (k === 'Estimate' || k === 'Quotation' || k === 'Invoice') return k as DocumentKind;
    return null;
  };

  const kindFromQueryRaw = searchParams.get('kind');
  const normalizedFromQuery = normalizeKind(kindFromQueryRaw);
  const normalizedFromProp = normalizeKind(initialKind as unknown as string);
  const effectiveInitialKind: DocumentKind = normalizedFromQuery || normalizedFromProp || 'Quotation';
  
  const [orgs, setOrgs] = useState<Array<{ id: string; name: string; code: string }>>([]);
  const [organisationId, setOrganisationId] = useState<string>('');
  // Only brands offered for new work (Admin → Organisations), plus this document's current one
  const { brands } = useBrands();
  const offeredOrgs = useMemo(() => {
    if (brands.length === 0) return orgs;
    const selectable = new Set(brands.filter((brand) => brand.isSelectable).map((brand) => brand.id));
    return orgs.filter((org) => selectable.has(org.id) || org.id === organisationId);
  }, [brands, orgs, organisationId]);
  const [kind, setKind] = useState<DocumentKind>(effectiveInitialKind);
  const [lifecycleRef, setLifecycleRef] = useState('');
  const [refManuallyEdited, setRefManuallyEdited] = useState<boolean>(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [notes, setNotes] = useState('');
  const [fees, setFees] = useState<DocumentFeesConfig>({ adminPct: 0.10, adminCap: 200, contingencyPct: 0.10, profitPct: 0.10, developerPct: null });
  const [depositAmount, setDepositAmount] = useState<number>(0);
  const [depositPct, setDepositPct] = useState<number>(0.40);
  const [depositMode, setDepositMode] = useState<'percent' | 'fixed'>('percent');
  const [depositEnabled, setDepositEnabled] = useState<boolean>(true);
  const [depositFixed, setDepositFixed] = useState<number>(0);
  const [paymentStructureApplicable, setPaymentStructureApplicable] = useState<boolean>(false);
  const [paymentStructurePattern, setPaymentStructurePattern] = useState<string | null>(null);
  const [vatEnabled, setVatEnabled] = useState<boolean>(true);
  const [vatPct, setVatPct] = useState<number>(0.20);
  const [inputVatSelected, setInputVatSelected] = useState<boolean>(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [customerTitle, setCustomerTitle] = useState<string>('');
  const [loadedDocument, setLoadedDocument] = useState<any>(null);

  useEffect(() => {
    if (embedded || !isEditMode || !loadedDocument || !isWorkspaceDocument(loadedDocument)) return;
    navigate(`/docs/${loadedDocument.id}`, { replace: true });
  }, [embedded, isEditMode, loadedDocument, navigate]);
  const [projectData, setProjectData] = useState<any>(workspaceProjectProp || null);
  const [lines, setLines] = useState<DocumentLineInput[]>([]);
  const [displayDays, setDisplayDays] = useState<number | null>(null);
  const [displayHours, setDisplayHours] = useState<number | null>(null);
  const [issuedAt, setIssuedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoSelectedFromProject, setAutoSelectedFromProject] = useState(false);
  
  // Versioning state (edit mode)
  const [versions, setVersions] = useState<WizardVersion[]>([]);
  const [currentVersionId, setCurrentVersionId] = useState<string | null>(null);
  const [versionBaseline, setVersionBaseline] = useState<string>('');
  const [versionShareLinks, setVersionShareLinks] = useState<DocumentShareLinkRecord[]>([]);

  const toWizardVersion = (v: any): WizardVersion => {
    const lock = versionLockFromRecord(v);
    return {
      id: String(v.id),
      versionNumber: Number(v.versionNumber ?? v.version_number ?? 0),
      isActive: !!(v.isActive ?? v.is_active),
      isTransmitted: lock.isTransmitted,
      transmittedAt: (v.transmittedAt ?? v.transmitted_at) ? String(v.transmittedAt ?? v.transmitted_at) : null,
      overrideUnlocked: lock.overrideUnlocked,
      overrideUnlockedAt: (v.overrideUnlockedAt ?? v.override_unlocked_at)
        ? String(v.overrideUnlockedAt ?? v.override_unlocked_at)
        : null,
      visualSurveyId: v.visualSurveyId ? String(v.visualSurveyId) : (v.visual_survey_id ? String(v.visual_survey_id) : null),
    };
  };

  // Customer store
  const { customers, loading: customersLoading, loadCustomers } = useCustomerStore();

  // Lookups
  const [materials, setMaterials] = useState<Array<{ id: string; name: string; default_unit?: string | null; default_cost: number }>>([]);
  const [equipment, setEquipment] = useState<Array<{ id: string; name: string; default_unit?: string | null; default_cost: number }>>([]);
  const [labourRates, setLabourRates] = useState<Array<{ id: string; name: string; rate_type: 'hour' | 'day'; rate: number }>>([]);

  // New line input states per category
  const [matSelId, setMatSelId] = useState<string>('');
  const [eqSelId, setEqSelId] = useState<string>('');
  const [lbSelId, setLbSelId] = useState<string>('');
  const [isEditingRef, setIsEditingRef] = useState<boolean>(false);

  // Lookup modals state
  const [openMaterial, setOpenMaterial] = useState(false);
  const [openEquipment, setOpenEquipment] = useState(false);
  const [openLabour, setOpenLabour] = useState(false);
  const [openHelp, setOpenHelp] = useState(false);
  const [createHydrated, setCreateHydrated] = useState(false);
  const [leavePromptOpen, setLeavePromptOpen] = useState(false);
  const [leaveSaving, setLeaveSaving] = useState(false);
  const createAutoIdentityRef = useRef<{ organisationId: string; lifecycleRef: string } | null>(null);
  const pendingLeaveRef = useRef<{ proceed: () => void; afterSave: 'proceed' | 'keep' } | null>(null);
  const findProjectCustomerMatch = (project: any): Customer | undefined => {
    if (!project || customers.length === 0) return undefined;

    const projectCustomerId =
      project.customer_id?.toString?.() ||
      project.customer_id?.toString?.() ||
      project.customer_id;
    const projectCustomerEmail = project.customer_email?.toLowerCase?.();
    const projectCustomerName = project.customer_name?.trim().toLowerCase();

    let match =
      customers.find(c => c.id === projectCustomerId) ||
      customers.find(c => c.reference === projectCustomerId);

    if (!match && projectCustomerEmail) {
      match = customers.find(
        c => (c.email || '').toLowerCase() === projectCustomerEmail
      );
    }

    if (!match && projectCustomerName) {
      match = customers.find(
        c => (c.name || '').trim().toLowerCase() === projectCustomerName
      );
    }

    return match;
  };

  const applyHydratedVersion = useCallback((header: any, rawLines: any[] | null | undefined, versionId?: string) => {
    const editorLines = editorLinesFromMapped(mapApiLinesToEditor(rawLines), !!embedded);
    const hydrated = hydrateDepositAndFees({
      header,
      lines: editorLines,
      fallbackFees: { adminPct: 0.10, adminCap: 200, contingencyPct: 0.10, profitPct: 0.10, developerPct: null },
    });
    setTitle(hydrated.title);
    setDescription(hydrated.description || '');
    setFees(hydrated.fees);
    setVatEnabled(hydrated.vatEnabled);
    setVatPct(hydrated.vatPct);
    setInputVatSelected(hydrated.inputVatSelected);
    setDepositEnabled(hydrated.depositEnabled);
    setDepositAmount(hydrated.depositAmount);
    setDepositPct(hydrated.depositPct);
    setDepositMode(hydrated.depositMode);
    setDepositFixed(hydrated.depositFixed);
    setPaymentStructureApplicable(hydrated.paymentStructureApplicable);
    setPaymentStructurePattern(hydrated.paymentStructurePattern);
    setDisplayDays(hydrated.displayDays);
    setDisplayHours(hydrated.displayHours);
    setLines(editorLines);
    setVersionBaseline(stringifyVersionSnapshot(hydrated));
    if (versionId) {
      const lock = versionLockFromRecord(header);
      setVersions((prev) => prev.map((item) => (
        item.id === String(versionId) ? { ...item, ...lock } : item
      )));
    }
  }, [embedded]);

  // Load initial data
  useEffect(() => {
    (async () => {
      const list = await documentsService.listOrganisations();
      setOrgs(list);
      
      if (isEditMode && documentId) {
        try {
                     const doc = await documentsService.getDocument(documentId);
           if (doc?.accountId && doc.kind === 'Invoice') {
             // Account invoices are edited on their own page, never in the quote wizard
             navigate(`/invoicing/invoices/${doc.id}`, { replace: true });
             return;
           }
           if (doc) {
             setLoadedDocument(doc);
             setOrganisationId(doc.organisationId);
            setKind(doc.kind);
            setLifecycleRef(doc.lifecycleRef);
            
            if (doc.kind !== 'Invoice') {
              let [verList, activeVersion] = await Promise.all([
                documentsService.listVersions(documentId),
                documentsService.getActiveVersion(documentId),
              ]);
              setVersions(verList.map(toWizardVersion));
              
              if ((!verList || verList.length === 0) || !activeVersion?.header) {
                try {
                  const created = await documentsService.createVersionFromActive(documentId);
                  await documentsService.activateVersion(documentId, created.id);
                  const [verAfter, activeAfter] = await Promise.all([
                    documentsService.listVersions(documentId),
                    documentsService.getActiveVersion(documentId),
                  ]);
                  verList = verAfter;
                  activeVersion = activeAfter as any;
                  setVersions(verAfter.map(toWizardVersion));
                } catch (e) {
                  console.warn('Failed to ensure initial active version:', e);
                }
              }
              
              if (activeVersion?.header || preferredVersionId) {
                setNotes(doc.notes || '');
                setIssuedAt(doc.issuedAt ?? null);
                const preferred = preferredVersionId
                  ? verList.find((version) => version.id === preferredVersionId)
                  : null;
                if (preferred) {
                  const loaded = await documentsService.getVersion(preferred.id);
                  applyHydratedVersion(loaded.header, loaded.lines, preferred.id);
                  setCurrentVersionId(preferred.id);
                } else if (activeVersion?.header) {
                  applyHydratedVersion(activeVersion.header, activeVersion.lines, String(activeVersion.header.id));
                  setCurrentVersionId(activeVersion.header.id);
                }
              }
              onVersionsChanged?.();
            } else {
              setVersions([]);
              setNotes(doc.notes || '');
              setIssuedAt(doc.issuedAt ?? null);
              applyHydratedVersion(doc, doc.lines);
              setCurrentVersionId(undefined as any);
            }
            // Note: header/lines were already set above based on kind
            
            setSelectedCustomer(null);
          }
        } catch (err) {
          setError('Failed to load document for editing');
          console.error(err);
        }
      } else {
        // Reset to default values for new documents
        setFees({ adminPct: 0.10, adminCap: 200, contingencyPct: 0.10, profitPct: 0.10, developerPct: null });
        setVatEnabled(true);
        setVatPct(0.2);
        setInputVatSelected(false);
        // Deposit defaults: enabled by default, 40% in percent mode
        setDepositEnabled(true);
        setDepositPct(0.40);
        setDepositMode('percent');
        setDepositFixed(0);
        setDepositAmount(0);
        setDisplayDays(null);
        setDisplayHours(null);
        setLines([]);
        setNotes('');
        if (preferredInvoiceRole) {
          setDepositEnabled(false);
          setDepositPct(0);
          setDepositMode('percent');
          setDepositFixed(0);
          setDepositAmount(0);
          setFees({ adminPct: 0, adminCap: 0, contingencyPct: 0, profitPct: 0, developerPct: null });
          if (preferredSeedLines?.length) setLines(preferredSeedLines);
        } else if (await isWorkspaceSupplyOnly(projectId)) {
          // Supply only: no deposit, the full balance is due
          const deposit = defaultDepositTerms(true);
          setDepositEnabled(deposit.hasDeposit);
          setDepositPct(deposit.depositPercentage);
        }
        if (preferredTitle) {
          setTitle(preferredTitle);
        }
        
        // New documents start under the workspace's brand, else the default brand
        const brandId = projectId
          ? await resolveWorkspaceBrandId(projectId).catch(() => null)
          : (await organisationSettings.defaultBrand().catch(() => null))?.id || null;
        const ksg = list.find(o => o.id === brandId) || list.find(o => o.code === 'KSG') || list[0];
        if (ksg) {
          setOrganisationId(ksg.id);
          const workspaceRef = preferredLifecycleRef
            || (projectId ? await ensureProjectLifecycleRef(projectId).catch(() => '') : '');
          if (workspaceRef) {
            setLifecycleRef(workspaceRef);
            setRefManuallyEdited(true);
            createAutoIdentityRef.current = { organisationId: ksg.id, lifecycleRef: workspaceRef };
          } else {
            const preview = await documentsService.getSequencePreview(ksg.id);
            setLifecycleRef(preview);
            createAutoIdentityRef.current = { organisationId: ksg.id, lifecycleRef: preview };
          }
        }
        setCreateHydrated(true);
      }
      
       const [mats, eqp, labour] = await Promise.all([
         lookupsService.listMaterials(),
         lookupsService.listEquipment(),
         lookupsService.listLabourRates(),
       ]);
       setMaterials(mats as any);
       setEquipment(eqp as any);
       setLabourRates(labour as any);
       
       await loadCustomers();
    })();
  }, [isEditMode, documentId, projectId, preferredLifecycleRef, preferredTitle, preferredInvoiceRole, preferredSeedLines, preferredVersionId, applyHydratedVersion]);

  // Workspace create: keep title aligned if preferredTitle arrives after mount.
  useEffect(() => {
    if (isEditMode || !preferredTitle) return;
    setTitle((prev) => (String(prev || '').trim() ? prev : preferredTitle));
  }, [isEditMode, preferredTitle]);

  // Set selected customer when customers load
  useEffect(() => {
    if (customers.length === 0) return;
    if (isEditMode && loadedDocument) {
      const customer = customers.find((item) =>
        item.id?.toString() === loadedDocument.customerId?.toString()
      ) || customers.find((item) =>
        item.name === `${loadedDocument.customerFirstName || ''} ${loadedDocument.customerLastName || ''}`.trim()
      );
      if (customer) {
        setSelectedCustomer(customer);
        return;
      }
    }
    if (customerId) {
      const byProp = customers.find((item) => item.id?.toString() === customerId.toString());
      if (byProp) setSelectedCustomer(byProp);
    }
  }, [customers, isEditMode, loadedDocument, customerId]);

  // Load project data
  useEffect(() => {
    if (workspaceProjectProp) {
      setProjectData(workspaceProjectProp);
      return;
    }
    if (!projectId) return;
    (async () => {
      try {
        const projectResponse = await secureApi.getProject(projectId);
        const project = projectResponse?.data || projectResponse;
        setProjectData(project);
      } catch (error) {
        console.warn('Failed to load project data:', error);
      }
    })();
  }, [projectId, workspaceProjectProp]);

  // A workspace document's customer is always the workspace's, including when reopening one
  // saved before the workspace customer changed
  useEffect(() => {
    if (!lockCustomer || !projectData || customers.length === 0) return;
    const projectCustomer = findProjectCustomerMatch(projectData);
    if (projectCustomer && projectCustomer.id !== selectedCustomer?.id) {
      setSelectedCustomer(projectCustomer);
      setCustomerTitle(projectCustomer.title || '');
    }
  }, [lockCustomer, projectData, customers, selectedCustomer]);

  // Set customer from project
  useEffect(() => {
    if (!isEditMode && projectData && customers.length > 0) {
      if (!selectedCustomer) {
        const projectCustomer = findProjectCustomerMatch(projectData);
        if (projectCustomer) {
          setSelectedCustomer(projectCustomer);
          if (!customerTitle) setCustomerTitle(projectCustomer.title || '');
          setAutoSelectedFromProject(true);
        } else {
          setAutoSelectedFromProject(false);
        }
      }
    }
  }, [isEditMode, projectData, customers, selectedCustomer, customerTitle]);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!isEditMode || !documentId || kind === 'Invoice') {
        if (alive) setVersionShareLinks([]);
        return;
      }
      try {
        const links = await listDocumentShareLinks(documentId);
        if (!alive) return;
        setVersionShareLinks(links);
      } catch (error) {
        console.warn('Failed to load version share links:', error);
        if (alive) setVersionShareLinks([]);
      }
    })();
    return () => { alive = false; };
  }, [isEditMode, documentId, kind]);

  const getVersionSnapshotInput = (): VersionSnapshotInput => ({
    title,
    description,
    fees,
    vatEnabled,
    vatPct,
    inputVatSelected,
    depositEnabled,
    depositAmount,
    depositMode,
    depositPct,
    displayDays,
    displayHours,
    paymentStructureApplicable,
    paymentStructurePattern,
    lines,
  });

  const getVersionSnapshot = () => stringifyVersionSnapshot(getVersionSnapshotInput());

  const isVersionDirty = useMemo(() => {
    if (!isEditMode || !currentVersionId) return false;
    return !!versionBaseline && getVersionSnapshot() !== versionBaseline;
  }, [isEditMode, currentVersionId, title, description, fees, vatEnabled, vatPct, inputVatSelected, depositEnabled, depositAmount, depositPct, depositMode, displayDays, displayHours, paymentStructureApplicable, paymentStructurePattern, lines, versionBaseline]);

  const getCreateBodySnapshot = () => JSON.stringify({
    title: title.trim(),
    description: (description || '').trim(),
    notes: (notes || '').trim(),
    fees,
    vatEnabled,
    vatPct,
    inputVatSelected,
    depositEnabled,
    depositMode,
    depositPct,
    depositFixed,
    paymentStructureApplicable,
    paymentStructurePattern,
    displayDays,
    displayHours,
    lines: buildVersionSnapshot(getVersionSnapshotInput()).lines,
  });

  const isCreateDirty = useMemo(() => {
    if (isEditMode) return false;
    const bodyDirty = getCreateBodySnapshot() !== CREATE_BODY_DEFAULT;
    const auto = createAutoIdentityRef.current;
    const identityDirty = createHydrated && !!auto && (
      organisationId !== auto.organisationId ||
      lifecycleRef !== auto.lifecycleRef
    );
    return bodyDirty || identityDirty;
  }, [
    isEditMode,
    createHydrated,
    organisationId,
    lifecycleRef,
    title,
    description,
    notes,
    fees,
    vatEnabled,
    vatPct,
    inputVatSelected,
    depositEnabled,
    depositMode,
    depositPct,
    depositFixed,
    paymentStructureApplicable,
    paymentStructurePattern,
    displayDays,
    displayHours,
    lines,
  ]);

  const isEditDirty = useMemo(() => {
    if (!isEditMode || !loadedDocument) return false;
    const versionChanged = !!versionBaseline && getVersionSnapshot() !== versionBaseline;
    const notesChanged = (notes || '') !== (loadedDocument.notes || '');
    const orgChanged = String(organisationId || '') !== String(loadedDocument.organisationId || '');
    const refChanged = refManuallyEdited && lifecycleRef !== (loadedDocument.lifecycleRef || '');
    const customerChanged = !!(
      selectedCustomer?.id
      && loadedDocument.customerId
      && String(selectedCustomer.id) !== String(loadedDocument.customerId)
    );
    return versionChanged || notesChanged || orgChanged || refChanged || customerChanged;
  }, [
    isEditMode,
    loadedDocument,
    versionBaseline,
    title,
    description,
    notes,
    fees,
    vatEnabled,
    vatPct,
    inputVatSelected,
    depositEnabled,
    depositAmount,
    depositPct,
    depositMode,
    displayDays,
    displayHours,
    lines,
    organisationId,
    lifecycleRef,
    refManuallyEdited,
    selectedCustomer,
    paymentStructureApplicable,
    paymentStructurePattern,
  ]);

  const isDirty = isCreateDirty || isEditDirty;

  useEffect(() => {
    if (!onVersionLockChange) return;
    if (!isEditMode || !currentVersionId) {
      onVersionLockChange(null);
      return;
    }
    const version = versions.find((item) => item.id === currentVersionId);
    if (!version) {
      onVersionLockChange(null);
      return;
    }
    onVersionLockChange({
      id: version.id,
      versionNumber: version.versionNumber,
      isTransmitted: version.isTransmitted,
      overrideUnlocked: version.overrideUnlocked,
      visualSurveyId: version.visualSurveyId,
    });
  }, [isEditMode, currentVersionId, versions, onVersionLockChange]);

  useEffect(() => {
    if (!lockRefreshKey || !isEditMode || !documentId || kind === 'Invoice') return;
    let cancelled = false;
    (async () => {
      try {
        const verList = await documentsService.listVersions(documentId);
        if (!cancelled) setVersions(verList.map(toWizardVersion));
      } catch (err) {
        console.warn('Failed to refresh version lock state', err);
      }
    })();
    return () => { cancelled = true; };
  }, [lockRefreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const mergeLinesForSave = async (
    current: DocumentLineInput[],
    versionIdForPreserve?: string | null,
  ): Promise<DocumentLineInput[]> => {
    if (!embedded || kind === 'Invoice') return current;
    const commercial = current.filter((line) => !isVisualDesignerLine(line));
    const currentVd = current.filter(isVisualDesignerLine);
    if (visualSurveyAttachable && visualDesignerLines.length > 0) {
      return [...visualDesignerLines, ...commercial];
    }
    // Prefer preserving existing VD lines on the target version. Survey items are
    // owned by WorkspaceVisualDesignerCard — empty wizard VD lines must not wipe them.
    const preserveFrom = versionIdForPreserve || currentVersionId;
    if (preserveFrom) {
      try {
        const loaded = await documentsService.getVersion(preserveFrom);
        const existingVd = (loaded.lines || [])
          .map((l: any) => ({
            lineKind: 'other' as const,
            name: l.name,
            description: l.description,
            qty: Number(l.qty || 1),
            unit: l.unit || 'item',
            unitPrice: Number(l.unit_price || 0),
            vatInclusive: !!l.vat_inclusive,
            lineTotal: l.line_total != null ? Number(l.line_total) : 0,
            sortOrder: l.sort_order,
            metaJson: l.meta_json ?? null,
          }))
          .filter(isVisualDesignerLine);
        return [...existingVd, ...commercial];
      } catch {
        throw new Error('Could not read the current designs, so nothing was saved. Please try again.');
      }
    }
    // Create (no version yet): keep VD lines edited in this wizard.
    if (currentVd.length > 0) {
      return [...currentVd, ...commercial];
    }
    return commercial;
  };

  const bindVisualSurveyIfNeeded = async (versionId: string) => {
    if (!embedded || !visualSurveyAttachable || !visualSurveyId) return;
    const survey = await visualDesignerSurveysService.getById(visualSurveyId);
    // First/Final surveys are permanently paired once bound. Never rebind to a different version on save.
    if (survey.documentVersionId && survey.documentVersionId !== versionId) {
      return;
    }
    if (!survey.documentVersionId) {
      await visualDesignerSurveysService.bindToVersion(visualSurveyId, versionId);
    }
    // Only push wizard VD lines onto the version when the wizard actually carries them.
    if (visualDesignerLines.length > 0) {
      await visualDesignerSurveysService.projectToVersion(visualSurveyId, versionId);
    }
  };

  const resolveSaveVersionId = async (): Promise<string | null> => {
    const preferred = String(preferredVersionId || '').trim();
    if (preferred) return preferred;
    if (embedded && visualSurveyId) {
      try {
        const survey = await visualDesignerSurveysService.getById(visualSurveyId);
        if (survey.documentVersionId) return survey.documentVersionId;
      } catch {
        // fall through
      }
    }
    return currentVersionId ? String(currentVersionId) : null;
  };

  const saveCurrentVersion = async () => {
    const targetVersionId = await resolveSaveVersionId();
    if (!targetVersionId) return;
    const editingVersion = versions.find((item) => item.id === targetVersionId)
      || versions.find((item) => item.id === currentVersionId);
    if (kind !== 'Invoice' && editingVersion?.isTransmitted && !editingVersion.overrideUnlocked) {
      throw new Error('This transmitted version is locked. Create a new version or unlock override to edit it.');
    }
    const headerPatch = {
      title,
      description,
      adminPct: fees.adminPct,
      adminCap: fees.adminCap,
      contingencyPct: fees.contingencyPct,
      profitPct: fees.profitPct,
      vatEnabled,
      vatPct,
      inputVatSelected,
      hasDeposit: depositEnabled,
      depositAmount,
      depositPercentage: depositMode === 'percent' ? depositPct : null,
      projectDays: displayDays,
      projectHours: displayHours,
      paymentStructureApplicable,
      paymentStructurePattern,
    };
    const plan = planVersionSaveLines({
      current: lines,
      embedded: !!embedded,
      isInvoice: kind === 'Invoice',
      attachingDesigns: visualSurveyAttachable ? visualDesignerLines : null,
    });
    await documentsService.saveVersion(targetVersionId, headerPatch, plan.lines, { keepDesigns: plan.keepDesigns });
    if (visualSurveyAttachable && visualSurveyId) {
      await bindVisualSurveyIfNeeded(targetVersionId);
    }
    if (currentVersionId !== targetVersionId) {
      setCurrentVersionId(targetVersionId);
    }
    setVersionBaseline(getVersionSnapshot());
  };

  const addVersion = async () => {
    if (!documentId) return;
    const created = await documentsService.createVersionFromActive(documentId);
    await documentsService.activateVersion(documentId, created.id);
    const verList = await documentsService.listVersions(documentId);
    setVersions(verList.map(toWizardVersion));
    const loaded = await documentsService.getVersion(created.id);
    applyHydratedVersion(loaded.header, loaded.lines, created.id);
    setCurrentVersionId(created.id);
    onVersionsChanged?.();
  };

  const switchVersion = async (newVersionId: string) => {
    const loaded = await documentsService.getVersion(newVersionId);
    applyHydratedVersion(loaded.header, loaded.lines, newVersionId);
    setCurrentVersionId(newVersionId);
  };

  const totals = useMemo(() => {
    const result = computeTotals(lines, fees, { enabled: vatEnabled, pct: vatPct });
    return result;
  }, [lines, fees, vatEnabled, vatPct]);

  // Keep deposit amount synced
  useEffect(() => {
    const r2 = (v: number) => Math.round((v + Number.EPSILON) * 100) / 100;
    if (!depositEnabled) {
      setDepositAmount(0);
      return;
    }
    if (depositMode === 'percent') {
      setDepositAmount(r2(totals.grandTotal * depositPct));
    } else {
      setDepositAmount(r2(depositFixed || 0));
    }
  }, [depositEnabled, depositMode, depositPct, depositFixed, totals.grandTotal]);

  const handleCreate = async (): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);
      if (!organisationId) throw new Error('Select an organisation');
      if (!title.trim()) throw new Error('Title is required');
      // Require a customer for all new documents (Estimate, Quotation, Invoice)
      if (!isEditMode && !selectedCustomer?.id) {
        throw new Error('Customer is required');
      }
      let workspaceProject = projectData || workspaceProjectProp;
      if (projectId && !workspaceProject) {
        try {
          const projectResponse = await secureApi.getProject(projectId);
          workspaceProject = projectResponse?.data || projectResponse;
          setProjectData(workspaceProject);
        } catch (error) {
          console.warn('Failed to load project data:', error);
        }
      }
      assertCanIssueCommercialDocument({
        project: workspaceProject,
        customer: selectedCustomer,
        kind,
      });
      const customerIdentity = resolveDocumentCustomerIdentity({
        project: workspaceProject,
        customer: selectedCustomer,
        kind,
      });
      if (isEditMode && kind !== 'Invoice') {
        const lockVersionId = String(preferredVersionId || currentVersionId || '').trim();
        const editingVersion = versions.find((item) => item.id === lockVersionId);
        if (editingVersion?.isTransmitted && !editingVersion.overrideUnlocked) {
          throw new Error('This transmitted version is locked. Create a new version or unlock override to edit it.');
        }
      }

      let shouldRelockAfterSave = false;
      let relockVersionId: string | null = null;
      
      if (isEditMode && documentId) {
        const previousOrganisationId = String((loadedDocument as any)?.organisationId || '');
        const nextOrganisationId = String(organisationId || '');
        const organisationChanged =
          previousOrganisationId.length > 0 &&
          nextOrganisationId.length > 0 &&
          previousOrganisationId !== nextOrganisationId;
        if (organisationChanged) {
          const lockVersionId = String(preferredVersionId || currentVersionId || '').trim();
          const editingVersion = versions.find((item) => item.id === lockVersionId)
            || versions.find((item) => item.id === currentVersionId);
          if (kind !== 'Invoice' && editingVersion?.isTransmitted && !editingVersion.overrideUnlocked) {
            throw new Error('Organisation is locked with this document. Unlock override to change it.');
          }
        }

        {
          const lockVersionId = String(preferredVersionId || currentVersionId || '').trim();
          const editingVersion = versions.find((item) => item.id === lockVersionId)
            || versions.find((item) => item.id === currentVersionId);
          if (
            kind !== 'Invoice'
            && editingVersion?.isTransmitted
            && editingVersion?.overrideUnlocked
            && editingVersion?.id
          ) {
            shouldRelockAfterSave = true;
            relockVersionId = editingVersion.id;
          }
        }

        let lifecycleRefPatch: string | undefined;
        if (lockLifecycleRef) {
          lifecycleRefPatch = undefined;
        } else if (refManuallyEdited) {
          lifecycleRefPatch = lifecycleRef;
        } else if (organisationChanged) {
          lifecycleRefPatch = await documentsService.mintLifecycleRef(nextOrganisationId);
          setLifecycleRef(lifecycleRefPatch);
        }

        // Persist edits
        if (kind === 'Invoice') {
          // Invoices are versionless: update base document header fields and replace base lines
          await documentsService.updateDocument(documentId, {
            kind,
            ...(lifecycleRefPatch !== undefined ? { lifecycleRef: lifecycleRefPatch } : {}),
            organisationId,
            ...(projectId !== undefined && { projectId }),
            customerId: selectedCustomer?.id || null,
            customerFirstName: customerIdentity.firstName,
            customerLastName: customerIdentity.lastName,
            customerTitle: customerIdentity.title || customerTitle || selectedCustomer?.title || null,
            title,
            description,
            notes,
            fees,
            vatEnabled,
            vatPct,
            inputVatSelected,
            hasDeposit: depositEnabled,
            depositAmount,
            depositPercentage: depositMode === 'percent' ? depositPct : null,
            projectDays: displayDays,
            projectHours: displayHours,
            paymentStructureApplicable,
            paymentStructurePattern,
          });
          await documentsService.replaceLines(documentId, lines);
        } else {
          // Estimates/Quotations: update doc metadata and save active version
          await documentsService.updateDocument(documentId, {
            kind,
            ...(lifecycleRefPatch !== undefined ? { lifecycleRef: lifecycleRefPatch } : {}),
            organisationId,
            ...(projectId !== undefined && { projectId }),
            customerId: selectedCustomer?.id || null,
            customerFirstName: customerIdentity.firstName,
            customerLastName: customerIdentity.lastName,
            customerTitle: customerIdentity.title || customerTitle || selectedCustomer?.title || null,
            notes,
          });
          await saveCurrentVersion();
        }
        if (shouldRelockAfterSave && relockVersionId) {
          await documentsService.relockVersionOverride(relockVersionId);
          try {
            const verList = await documentsService.listVersions(documentId);
            setVersions(verList.map(toWizardVersion));
          } catch (err) {
            console.warn('Failed to refresh versions after relock', err);
          }
          onVersionsChanged?.();
        }
        if (embedded) {
          if (documentId) onDocumentReady?.(documentId);
        } else {
          navigate(`/docs/${documentId}`);
        }
      } else {
        let finalLifecycleRef = lifecycleRef;
        if (lockLifecycleRef && preferredLifecycleRef) {
          finalLifecycleRef = preferredLifecycleRef;
        } else if (projectId && (lockLifecycleRef || !refManuallyEdited)) {
          finalLifecycleRef = await ensureProjectLifecycleRef(projectId);
        } else if (!refManuallyEdited && organisationId) {
          finalLifecycleRef = await documentsService.mintLifecycleRef(organisationId);
        } else if (organisationId && lifecycleRef) {
          const exists = await documentsService.refExists(organisationId, kind, lifecycleRef);
          if (exists) throw new Error(`Reference ${lifecycleRef} already exists for ${kind}.`);
        }

        const mergedCreateLines = await mergeLinesForSave(lines);
        const doc = await documentsService.createDocument({
          kind,
          lifecycleRef: finalLifecycleRef,
          organisationId,
          isOriginal: kind !== 'Invoice' ? true : true, // standalone create is origin; invoices created here are standalone
          projectId: projectId ?? null,
          customerId: selectedCustomer?.id || null,
          customerFirstName: customerIdentity.firstName,
          customerLastName: customerIdentity.lastName,
          customerTitle: customerIdentity.title || customerTitle || selectedCustomer?.title || null,
          title,
          description,
          notes,
          status: 'draft',
          currency: 'GBP',
          depositAmount,
          depositPercentage: depositMode === 'percent' ? depositPct : null,
          hasDeposit: preferredInvoiceRole ? false : depositEnabled,
          projectDays: displayDays,
          projectHours: displayHours,
          paymentStructureApplicable,
          paymentStructurePattern,
          source: 'jarvis',
          vatEnabled,
          vatPct,
          inputVatSelected,
          fees,
          invoiceRole: preferredInvoiceRole ?? null,
        }, mergedCreateLines);
        if (embedded && kind !== 'Invoice' && projectId) {
          try {
            // First Survey may already hold VD items from the bottom accordion; bind + project.
            await visualDesignerSurveysService.ensureFirstSurveyBoundToQuote(String(projectId), doc.id);
          } catch (bindErr) {
            console.warn('Document saved but Visual Designer pairing failed:', bindErr);
          }
        }
        if (embedded) {
          onDocumentReady?.(doc.id);
        } else {
          navigate(`/docs/${doc.id}`);
        }
      }
      return true;
    } catch (e: any) {
      console.error('Error saving document:', {
        error: e,
        message: e?.message,
        stack: e?.stack,
        documentId,
        isEditMode,
        kind,
      });
      setError(e?.message || 'Failed to save document');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const requestLeave = useCallback((proceed: () => void, options?: { afterSave?: 'proceed' | 'keep' }) => {
    if (!isDirty) {
      proceed();
      return;
    }
    pendingLeaveRef.current = { proceed, afterSave: options?.afterSave ?? 'proceed' };
    setLeavePromptOpen(true);
  }, [isDirty]);

  const requestLeaveRef = useRef(requestLeave);
  requestLeaveRef.current = requestLeave;
  const handleCreateRef = useRef(handleCreate);
  handleCreateRef.current = handleCreate;
  const openHelpRef = useRef(() => setOpenHelp(true));
  openHelpRef.current = () => setOpenHelp(true);

  useLayoutEffect(() => {
    if (!leaveGuardRef) return;
    leaveGuardRef.current = {
      requestLeave: (proceed, options) => requestLeaveRef.current(proceed, options),
      save: () => handleCreateRef.current(),
      openHelp: () => openHelpRef.current(),
    };
    return () => {
      leaveGuardRef.current = null;
    };
  }, [leaveGuardRef]);

  useEffect(() => {
    if (!embedded || !isDirty) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [embedded, isDirty]);

  useEffect(() => {
    if (!embedded || !isDirty) return;
    const onClickCapture = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = (event.target as HTMLElement | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!anchor || anchor.target === '_blank' || anchor.hasAttribute('download')) return;
      const url = new URL(anchor.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      if (url.pathname === window.location.pathname && url.search === window.location.search) return;
      event.preventDefault();
      event.stopPropagation();
      requestLeaveRef.current(() => navigate(`${url.pathname}${url.search}${url.hash}`));
    };
    document.addEventListener('click', onClickCapture, true);
    return () => document.removeEventListener('click', onClickCapture, true);
  }, [embedded, isDirty, navigate]);

  const closeLeavePrompt = () => {
    pendingLeaveRef.current = null;
    setLeavePromptOpen(false);
  };

  const handleLeaveDiscard = () => {
    const pending = pendingLeaveRef.current;
    pendingLeaveRef.current = null;
    setLeavePromptOpen(false);
    pending?.proceed();
  };

  const handleLeaveSave = async () => {
    setLeaveSaving(true);
    const ok = await handleCreate();
    setLeaveSaving(false);
    if (!ok) {
      pendingLeaveRef.current = null;
      setLeavePromptOpen(false);
      return;
    }
    const pending = pendingLeaveRef.current;
    pendingLeaveRef.current = null;
    setLeavePromptOpen(false);
    if (pending?.afterSave === 'proceed') pending.proceed();
  };

  const leaveKindLabel = kind === 'Quotation' ? 'quote' : kind.toLowerCase();

  const sharedProps = {
    kind,
    setKind,
    lifecycleRef,
    setLifecycleRef,
    title,
    setTitle,
    description,
    setDescription,
    notes,
    setNotes,
    fees,
    setFees,
    selectedCustomer,
    setSelectedCustomer,
    customerTitle,
    setCustomerTitle,
    depositAmount,
    setDepositAmount,
    depositPct,
    setDepositPct,
    depositMode,
    setDepositMode,
    depositEnabled,
    setDepositEnabled,
    depositFixed,
    setDepositFixed,
    paymentStructureApplicable,
    setPaymentStructureApplicable,
    paymentStructurePattern,
    setPaymentStructurePattern,
    vatEnabled,
    setVatEnabled,
    vatPct,
    setVatPct,
    inputVatSelected,
    setInputVatSelected,
    displayDays,
    setDisplayDays,
    displayHours,
    setDisplayHours,
    lines,
    setLines,
    materials,
    equipment,
    labourRates,
    orgs,
    organisationId,
    setOrganisationId,
    customers,
    customersLoading,
    projectData,
    loading,
    error,
    isEditingRef,
    setIsEditingRef,
    refManuallyEdited,
    setRefManuallyEdited,
  };

  const visualPairing: VisualDesignerPairingModel | undefined =
    embedded && kind !== 'Invoice' && onOpenVisualDesigner
      ? {
          surveys: visualSurveys,
          selectedSurveyId: visualSurveyId,
          selectedSurveyLabel: visualSurveyLabel,
          itemCount: visualDesignerLines.length,
          pairingLocked: visualDesignerPairingLocked,
          attachable: visualSurveyAttachable,
          onSelectSurvey: (id) => onVisualSurveyChange?.(id),
          onOpenVisualDesigner,
        }
      : undefined;

  const lockVersionId = String(preferredVersionId || currentVersionId || '').trim();
  const lockedVersion = versions.find((item) => item.id === lockVersionId)
    || versions.find((item) => item.id === currentVersionId)
    || versions.find((item) => item.isActive);
  const documentLocked = !!(
    isEditMode
    && kind !== 'Invoice'
    && lockedVersion?.isTransmitted
    && !lockedVersion?.overrideUnlocked
  );
  /** Organisation is identity for the document; keep it editable until the version is locked. */
  const organisationLocked = documentLocked;

  return (
    <Box sx={embedded ? {
      width: '100%',
      mx: 0,
      px: 0,
    } : { 
      width: { xs: '100%', sm: '100%', md: '100%', lg: '88%', xl: '72%' }, 
      mx: 'auto', 
      px: { xs: 1, sm: 2 } 
    }}>
      {error && <Typography color="danger" sx={{ mb: 1 }}>{error}</Typography>}

      {/* Customer Reference Card */}
      {embedded ? (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: lockLifecycleRef ? '1fr' : '1fr 1fr' }, columnGap: 2, rowGap: 1 }}>
            <FormControl>
              <FormLabel>Organisation</FormLabel>
              <Select
                value={organisationId}
                disabled={organisationLocked}
                onChange={async (_, v) => {
                  if (organisationLocked) return;
                  const id = String(v || '');
                  setOrganisationId(id);
                  if (id && !lockLifecycleRef) {
                    const preview = await documentsService.getSequencePreview(id);
                    setLifecycleRef(preview);
                  }
                }}
                placeholder="Select organisation"
              >
                {offeredOrgs.map(o => <Option key={o.id} value={o.id}>{o.name} ({o.code})</Option>)}
              </Select>
              {organisationLocked && (
                <Typography level="body-xs" sx={{ mt: 0.5, color: 'neutral.500' }}>
                  Locked with this document. Unlock override to change organisation.
                </Typography>
              )}
            </FormControl>
            {!lockLifecycleRef && (
            <FormControl>
              <FormLabel>Reference</FormLabel>
              <Stack direction="row" spacing={1} alignItems="center">
                <Input size="sm" placeholder="Reference" value={lifecycleRef} onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setLifecycleRef(e.target.value); if (isEditingRef) setRefManuallyEdited(true); }} disabled={!isEditingRef} sx={{ flex: 1 }} />
                {!isEditingRef ? (
                  <Button size="sm" variant="outlined" onClick={() => setIsEditingRef(true)}>Edit</Button>
                ) : (
                  <Button size="sm" variant="solid" onClick={() => setIsEditingRef(false)}>Done</Button>
                )}
              </Stack>
            </FormControl>
            )}
          </Box>
        </Box>
      ) : (
      <Card sx={{ p: 2, mb: 2 }}>
        <Typography level="title-md" sx={{ mb: 1 }}>Customer Reference</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 2, rowGap: 1 }}>
          <Box>
          <FormControl>
            <FormLabel>Organisation</FormLabel>
            <Select
              value={organisationId}
              disabled={organisationLocked}
              onChange={async (_, v) => {
                if (organisationLocked) return;
                const id = String(v || '');
                setOrganisationId(id);
                if (id) {
                  const preview = await documentsService.getSequencePreview(id);
                  setLifecycleRef(preview);
                }
              }}
              placeholder="Select organisation"
            >
              {offeredOrgs.map(o => <Option key={o.id} value={o.id}>{o.name} ({o.code})</Option>)}
            </Select>
            {organisationLocked && (
              <Typography level="body-xs" sx={{ mt: 0.5, color: 'neutral.500' }}>
                Locked with this document. Unlock override to change organisation.
              </Typography>
            )}
          </FormControl>
          <FormControl>
            <FormLabel>Document Type</FormLabel>
            {lockKind ? (
              <Typography level="body-sm" sx={{ py: 0.75 }}>{kind}</Typography>
            ) : (
              <Select value={kind} onChange={(_, v) => v && setKind(v as DocumentKind)}>
                <Option value="Estimate">Estimate</Option>
                <Option value="Quotation">Quotation</Option>
                <Option value="Invoice">Invoice</Option>
              </Select>
            )}
          </FormControl>
          </Box>
          <Box>
            <FormControl error={!selectedCustomer}>
              <FormLabel>Customer *</FormLabel>
              {lockCustomer ? (
                <>
                  <Typography level="body-sm" sx={{ py: 0.75 }}>
                    {selectedCustomer
                      ? `${selectedCustomer.name}${selectedCustomer.email ? ` (${selectedCustomer.email})` : ''}`
                      : 'Inherited from Overview'}
                  </Typography>
                  <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                    Follows the workspace customer. Change it from Overview.
                  </Typography>
                </>
              ) : (
                <Autocomplete<Customer, false, false, false>
                  placeholder="Search for a customer..."
                  options={customers}
                  getOptionLabel={(option) => typeof option === 'string' ? option : `${option.name} (${option.phone || 'No phone'})`}
                  value={selectedCustomer}
                  onChange={(_, newValue) => {
                    setSelectedCustomer(newValue as Customer);
                    setAutoSelectedFromProject(false);
                  }}
                  loading={customersLoading}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                />
              )}
              {!selectedCustomer && (
                <Typography level="body-xs" color="danger" sx={{ mt: 0.5 }}>Customer is required</Typography>
              )}
            </FormControl>
            <FormControl>
              <FormLabel>Reference</FormLabel>
              <Stack direction="row" spacing={1} alignItems="center">
                <Input size="sm" placeholder="Reference" value={lifecycleRef} onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setLifecycleRef(e.target.value); if (isEditingRef) setRefManuallyEdited(true); }} disabled={!isEditingRef} sx={{ flex: 1 }} />
                {!isEditingRef ? (
                  <Button size="sm" variant="outlined" onClick={() => setIsEditingRef(true)}>Edit</Button>
                ) : (
                  <Button size="sm" variant="solid" onClick={() => setIsEditingRef(false)}>Done</Button>
                )}
              </Stack>
            </FormControl>
            {projectData && selectedCustomer && autoSelectedFromProject && (
              <Box sx={{ mt: 1, p: 1, bgcolor: 'success.softBg', borderRadius: 'sm', border: '1px solid', borderColor: 'success.softBorder' }}>
                <Typography level="body-sm" sx={{ color: 'success.700', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span style={{ fontSize: '16px' }}>ℹ️</span>
                  Customer automatically selected from project: {projectData.project_name || 'Project'}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
        <Box sx={{ mt: 2 }}>
          <Typography level="title-sm" sx={{ mb: 1 }}>Internal Notes</Typography>
          <FormControl>
            <Textarea
              minRows={4}
              value={notes}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNotes(e.target.value)}
              placeholder="Add internal notes (never included in customer/public/supplier outputs)..."
            />
            <Typography level="body-xs" sx={{ mt: 0.75, color: 'text.secondary' }}>
              Internal use only. This note is shared across all document versions but is not included in external views</Typography>
          </FormControl>
        </Box>
        </Card>
      )}

      {/* Render appropriate wizard based on mode */}
      {isEditMode ? (
        <EditWizard
          {...sharedProps}
          documentId={documentId!}
          issuedAt={issuedAt}
          versions={versions}
          versionShareLinks={versionShareLinks}
          currentVersionId={currentVersionId}
          isVersionDirty={isVersionDirty}
          handleCreate={handleCreate}
          versionLayout={versionLayout}
          omitVisualDesigner={embedded}
          omitLockControls={omitLockControls || hideVersions}
          omitActionButtons={embedded}
          hideVersions={hideVersions}
          visualPairing={hideVersions ? undefined : visualPairing}
          onCancel={embedded ? () => requestLeave(() => onClose?.(), { afterSave: 'keep' }) : undefined}
          onOpenMaterial={() => setOpenMaterial(true)}
          onOpenEquipment={() => setOpenEquipment(true)}
          onOpenLabour={() => setOpenLabour(true)}
          onOpenHelp={() => setOpenHelp(true)}
          onVersionChange={switchVersion}
          onAddVersion={addVersion}
          onActivateVersion={async () => {
            try {
              await documentsService.activateVersion(documentId!, currentVersionId!);
              const verList = await documentsService.listVersions(documentId!);
              setVersions(verList.map(toWizardVersion));
              onVersionsChanged?.();
                    } catch (e: any) {
                      alert(`Failed to set active: ${e.message}`);
                    }
          }}
          onDeleteVersion={async () => {
                    const ok = window.confirm('Delete this version? This cannot be undone.');
                    if (!ok) return;
                    try {
              await documentsService.deleteVersion(currentVersionId!);
              const verList = await documentsService.listVersions(documentId!);
                      setVersions(verList.map(toWizardVersion));
                      const active = verList.find(v => v.isActive) || verList[0];
                      if (active) {
                        await switchVersion(active.id);
                      }
              onVersionsChanged?.();
                    } catch (e: any) {
                      alert(`Failed to delete: ${e.message}`);
            }
          }}
          onUnlockVersionOverride={async (reason?: string) => {
            if (!currentVersionId) return;
            try {
              const result = await documentsService.unlockVersionOverride(currentVersionId, reason);
              const verList = await documentsService.listVersions(documentId!);
              setVersions(verList.map(toWizardVersion));
              if (result.forked && result.workingVersionId) {
                await switchVersion(result.workingVersionId);
              }
              onVersionsChanged?.();
            } catch (e: any) {
              alert(`Failed to unlock override: ${e?.message || 'Unknown error'}`);
            }
          }}
          onRelockVersionOverride={async () => {
            if (!currentVersionId) return;
            try {
              await documentsService.relockVersionOverride(currentVersionId);
              const verList = await documentsService.listVersions(documentId!);
              setVersions(verList.map(toWizardVersion));
              onVersionsChanged?.();
            } catch (e: any) {
              alert(`Failed to relock version: ${e?.message || 'Unknown error'}`);
            }
          }}
        />
      ) : (
        <CreateWizard
          {...sharedProps}
          handleCreate={handleCreate}
          omitVisualDesigner={embedded}
          omitActionButtons={embedded}
          visualPairing={hideVersions || embedded ? undefined : visualPairing}
          onCancel={embedded ? () => requestLeave(() => onClose?.(), { afterSave: 'keep' }) : undefined}
          onOpenMaterial={() => setOpenMaterial(true)}
          onOpenEquipment={() => setOpenEquipment(true)}
          onOpenLabour={() => setOpenLabour(true)}
          onOpenHelp={() => setOpenHelp(true)}
        />
      )}

      {/* Modals for managing lookups */}
      <Modal open={openMaterial} onClose={() => setOpenMaterial(false)}>
        <ModalDialog sx={{ maxWidth: { xs: '95vw', sm: '80vw', md: '70vw' }, maxHeight: { xs: '90vh', sm: '80vh' } }}>
          <DialogTitle>Manage Materials</DialogTitle>
          <DialogContent>
            <LookupAdminPanel initialFilter="materials" locked onSaved={(k, saved) => {
              if (saved?.id) setMatSelId(saved.id);
            }} onDataChanged={async () => {
              const [mats, eqp, labour] = await Promise.all([
                lookupsService.listMaterials(),
                lookupsService.listEquipment(),
                lookupsService.listLabourRates(),
              ]);
              setMaterials(mats as any);
              setEquipment(eqp as any);
              setLabourRates(labour as any);
            }} />
          </DialogContent>
          <DialogActions>
            <Button onClick={async () => {
              setOpenMaterial(false);
              const [mats, eqp, labour] = await Promise.all([
                lookupsService.listMaterials(),
                lookupsService.listEquipment(),
                lookupsService.listLabourRates(),
              ]);
              setMaterials(mats as any);
              setEquipment(eqp as any);
              setLabourRates(labour as any);
            }}>Close</Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      <Modal open={openEquipment} onClose={() => setOpenEquipment(false)}>
        <ModalDialog sx={{ maxWidth: { xs: '95vw', sm: '80vw', md: '70vw' }, maxHeight: { xs: '90vh', sm: '80vh' } }}>
          <DialogTitle>Manage Equipment</DialogTitle>
          <DialogContent>
            <LookupAdminPanel initialFilter="equipment" locked onSaved={(k, saved) => {
              if (saved?.id) setEqSelId(saved.id);
            }} onDataChanged={async () => {
              const [mats, eqp, labour] = await Promise.all([
                lookupsService.listMaterials(),
                lookupsService.listEquipment(),
                lookupsService.listLabourRates(),
              ]);
              setMaterials(mats as any);
              setEquipment(eqp as any);
              setLabourRates(labour as any);
            }} />
          </DialogContent>
          <DialogActions>
            <Button onClick={async () => {
              setOpenEquipment(false);
              const [mats, eqp, labour] = await Promise.all([
                lookupsService.listMaterials(),
                lookupsService.listEquipment(),
                lookupsService.listLabourRates(),
              ]);
              setMaterials(mats as any);
              setEquipment(eqp as any);
              setLabourRates(labour as any);
            }}>Close</Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      <Modal open={openLabour} onClose={() => setOpenLabour(false)}>
        <ModalDialog sx={{ maxWidth: { xs: '95vw', sm: '80vw', md: '70vw' }, maxHeight: { xs: '90vh', sm: '80vh' } }}>
          <DialogTitle>Manage Labour Rates</DialogTitle>
          <DialogContent>
            <LookupAdminPanel initialFilter="labour" locked onSaved={(k, saved) => {
              if (saved?.id) setLbSelId(saved.id);
            }} onDataChanged={async () => {
              const [mats, eqp, labour] = await Promise.all([
                lookupsService.listMaterials(),
                lookupsService.listEquipment(),
                lookupsService.listLabourRates(),
              ]);
              setMaterials(mats as any);
              setEquipment(eqp as any);
              setLabourRates(labour as any);
            }} />
          </DialogContent>
          <DialogActions>
            <Button onClick={async () => {
              setOpenLabour(false);
              const [mats, eqp, labour] = await Promise.all([
                lookupsService.listMaterials(),
                lookupsService.listEquipment(),
                lookupsService.listLabourRates(),
              ]);
              setMaterials(mats as any);
              setEquipment(eqp as any);
              setLabourRates(labour as any);
            }}>Close</Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      {/* Fee edit modals */}
      <Modal open={openHelp} onClose={() => setOpenHelp(false)}>
        <ModalDialog sx={{ maxWidth: 720 }}>
          <DialogTitle>How these totals are calculated</DialogTitle>
          <DialogContent>
            <Stack spacing={1}>
              <Typography level="title-sm">1) Base Cost</Typography>
              <Typography level="body-sm">Sum of Materials + Equipment + Labour + Additional Costs.</Typography>
              <Typography level="title-sm">2) Company Additions</Typography>
              <Typography level="body-sm">Admin fee = min(Base × Admin %, Admin Cap). Contingency = Base × Contingency %.</Typography>
              <Typography level="title-sm">3) Profit margin</Typography>
              <Typography level="body-sm">Calculated on Base only: Profit margin = Base × Profit %.</Typography>
              <Typography level="title-sm">4) Subtotal (pre‑VAT)</Typography>
              <Typography level="body-sm">Subtotal = Base + Admin fee + Contingency + Profit margin.</Typography>
              <Typography level="title-sm">5) VAT</Typography>
              <Typography level="body-sm">If Charge VAT is on, VAT = Subtotal × VAT rate (20% by default). For business view, we also show Materials and Labour incl. VAT breakdown.</Typography>
              <Typography level="title-sm">6) Total</Typography>
              <Typography level="body-sm">Total (incl. VAT) = Subtotal + VAT. Total (excl. VAT) = Subtotal.</Typography>
              <Typography level="title-sm">7) Profit (displayed)</Typography>
              <Typography level="body-sm">Profit (Admin + Contingency + Profit Margin): this is Admin fee + Contingency + the Profit margin (which is Base × Profit%).</Typography>
              <Typography level="title-sm">8) Input VAT (goods only)</Typography>
              <Typography level="body-sm">For goods lines (materials/equipment/additional): if a line is VAT‑inclusive, its VAT is Gross × (rate / (1 + rate)) (at 20% this is Gross ÷ 6). If VAT‑exclusive, VAT = Net × rate.</Typography>
              <Typography level="title-sm">9) Profit Per Day</Typography>
              <Typography level="body-sm">Profit Per Day = Profit (- all overheads) ÷ display days. If days are entered, we round up to a whole day (for example, 1.5 days becomes 2). If only hours are entered, this counts as 1 day.</Typography>
              <Typography level="body-sm">The label is shown as "Profit Per Day (X day(s))", where X is the effective duration used in the calculation. If no project duration is provided, we show "please populate project duration" instead of a calculated per-day value.</Typography>
              <Typography level="title-sm">10) Magic Number</Typography>
              <Typography level="body-sm">We show the saved daily revenue target (Magic Number) and how your pre‑VAT and post‑VAT totals compare, with equations.</Typography>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenHelp(false)}>Close</Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      <Modal open={leavePromptOpen} onClose={() => { if (!leaveSaving) closeLeavePrompt(); }}>
        <ModalDialog sx={{ maxWidth: 440, width: '100%' }}>
          <DialogTitle>Save this {leaveKindLabel}?</DialogTitle>
          <DialogContent>
            You have unsaved changes. Save before leaving, or discard them.
          </DialogContent>
          <DialogActions>
            <Button variant="plain" color="neutral" disabled={leaveSaving} onClick={closeLeavePrompt}>
              Stay
            </Button>
            <Button variant="outlined" color="neutral" disabled={leaveSaving} onClick={handleLeaveDiscard}>
              Don&apos;t save
            </Button>
            <Button variant="solid" loading={leaveSaving} onClick={() => { void handleLeaveSave(); }}>
              Save
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>
      
    </Box>
  );
};

export default EstimateQuoteWizard;
